/* eslint-disable no-restricted-syntax */
/**
 * useLinkingState - Computes sorted entity lists and resolved links
 *
 * Sorting Logic:
 * 1. Approved links → Top (entities aligned horizontally by link order)
 * 2. Suggested links → Middle (entities aligned horizontally by confidence desc)
 * 3. Unlinked entities → Bottom (no alignment, sorted by name)
 */

import { useMemo } from 'react';

import type { FinchEntity } from '@/services/api/entitySyncService';
import type { DetailedFilevineCollectionItemSchema } from '@/services/api/generated/entitySyncService';

import type {
  ConnectionStatus,
  EntityLinkSchema,
  LinkingState,
  ResolvedLink,
  SortedEntity,
  UseLinkingStateReturn,
} from '../types/linking-view.types';

interface UseLinkingStateProps {
  finchEntities: FinchEntity[];
  filevineEntities: DetailedFilevineCollectionItemSchema[];
  suggestedLinks: EntityLinkSchema[];
  approvedLinks: EntityLinkSchema[];
}

/**
 * Get entity name for sorting
 */
function getFinchEntityName(entity: FinchEntity): string {
  return 'name' in entity ? entity.name : '';
}

/**
 * Get primary display field from Filevine entity for sorting
 */
function getFilevineEntityName(
  entity: DetailedFilevineCollectionItemSchema,
): string {
  const data = entity.latest_snapshot?.data;
  if (!data) return '';

  // Try common name fields
  // eslint-disable-next-line no-restricted-syntax
  for (const key of [
    'Name',
    'name',
    'FullName',
    'fullName',
    'Title',
    'title',
  ]) {
    if (data[key] && typeof data[key] === 'string') {
      return data[key] as string;
    }
  }

  return '';
}

/**
 * Create entity lookup maps
 */
function createEntityMaps(
  finchEntities: FinchEntity[],
  filevineEntities: DetailedFilevineCollectionItemSchema[],
): {
  finchMap: Map<string, FinchEntity>;
  filevineMap: Map<string, DetailedFilevineCollectionItemSchema>;
} {
  const finchMap = new Map(finchEntities.map((e) => [e.id, e]));
  const filevineMap = new Map(filevineEntities.map((e) => [e.id, e]));
  return { finchMap, filevineMap };
}

/**
 * Resolve links to include full entity data
 */
function resolveLinks(
  links: EntityLinkSchema[],
  finchMap: Map<string, FinchEntity>,
  filevineMap: Map<string, DetailedFilevineCollectionItemSchema>,
  connectionStatus: ConnectionStatus,
): ResolvedLink[] {
  return links
    .map((link) => {
      const finchEntity = finchMap.get(link.finch_entity_id);
      // filevine_entity_id can be null for IGNORED links
      const filevineEntity = link.filevine_entity_id
        ? filevineMap.get(link.filevine_entity_id)
        : undefined;

      if (!finchEntity || !filevineEntity) {
        return null;
      }

      return {
        id: link.id,
        finchEntity,
        filevineEntity,
        status: link.link_status,
        confidence: link.confidence,
        reasoning: link.reasoning,
        connectionStatus,
      };
    })
    .filter((link): link is ResolvedLink => link !== null);
}

/**
 * Sort entities by link status, then by name
 */
function sortEntities<T>(
  entities: T[],
  approvedLinks: ResolvedLink[],
  suggestedLinks: ResolvedLink[],
  getEntityId: (entity: T) => string,
  getEntityName: (entity: T) => string,
  side: 'finch' | 'filevine',
): SortedEntity<T>[] {
  // Create link lookup: entityId -> { linkId, pairedEntityId, order, isApproved }
  const linkInfo = new Map<
    string,
    {
      linkId: string;
      pairedEntityId: string;
      order: number;
      isApproved: boolean;
    }
  >();

  // Add approved links first (order 0-N)
  approvedLinks.forEach((link, index) => {
    const entityId =
      side === 'finch' ? link.finchEntity.id : link.filevineEntity.id;
    const pairedId =
      side === 'finch' ? link.filevineEntity.id : link.finchEntity.id;
    linkInfo.set(entityId, {
      linkId: link.id,
      pairedEntityId: pairedId,
      order: index,
      isApproved: true,
    });
  });

  // Add suggested links (order N+1 onwards), sorted by confidence desc
  const sortedSuggested = [...suggestedLinks].sort(
    (a, b) => b.confidence - a.confidence,
  );
  sortedSuggested.forEach((link, index) => {
    const entityId =
      side === 'finch' ? link.finchEntity.id : link.filevineEntity.id;
    const pairedId =
      side === 'finch' ? link.filevineEntity.id : link.finchEntity.id;
    // Don't overwrite if already has an approved link
    if (!linkInfo.has(entityId)) {
      linkInfo.set(entityId, {
        linkId: link.id,
        pairedEntityId: pairedId,
        order: approvedLinks.length + index,
        isApproved: false,
      });
    }
  });

  // Sort entities: linked first (by order), then unlinked (by name)
  const sorted = [...entities].sort((a, b) => {
    const aId = getEntityId(a);
    const bId = getEntityId(b);
    const aLink = linkInfo.get(aId);
    const bLink = linkInfo.get(bId);

    // Both linked: sort by link order
    if (aLink && bLink) {
      return aLink.order - bLink.order;
    }

    // One linked, one not: linked first
    if (aLink && !bLink) return -1;
    if (!aLink && bLink) return 1;

    // Neither linked: sort by name
    return getEntityName(a).localeCompare(getEntityName(b));
  });

  // Map to SortedEntity with display indices
  return sorted.map((entity, displayIndex) => {
    const entityId = getEntityId(entity);
    const link = linkInfo.get(entityId);

    return {
      entity,
      displayIndex,
      isLinked: !!link,
      linkId: link?.linkId ?? null,
      pairedEntityId: link?.pairedEntityId ?? null,
    };
  });
}

/**
 * Main hook for computing linking state
 */
export function useLinkingState({
  finchEntities,
  filevineEntities,
  suggestedLinks,
  approvedLinks,
}: UseLinkingStateProps): UseLinkingStateReturn {
  const state = useMemo<LinkingState>(() => {
    const { finchMap, filevineMap } = createEntityMaps(
      finchEntities,
      filevineEntities,
    );

    // Resolve links with full entity data
    const resolvedApproved = resolveLinks(
      approvedLinks,
      finchMap,
      filevineMap,
      'approved',
    );
    const resolvedSuggested = resolveLinks(
      suggestedLinks,
      finchMap,
      filevineMap,
      'suggested',
    );

    // Sort entities
    const sortedFinchEntities = sortEntities(
      finchEntities,
      resolvedApproved,
      resolvedSuggested,
      (e) => e.id,
      getFinchEntityName,
      'finch',
    );

    const sortedFilevineEntities = sortEntities(
      filevineEntities,
      resolvedApproved,
      resolvedSuggested,
      (e) => e.id,
      getFilevineEntityName,
      'filevine',
    );

    // Count unlinked entities
    const linkedFinchIds = new Set([
      ...resolvedApproved.map((l) => l.finchEntity.id),
      ...resolvedSuggested.map((l) => l.finchEntity.id),
    ]);
    const linkedFilevineIds = new Set([
      ...resolvedApproved.map((l) => l.filevineEntity.id),
      ...resolvedSuggested.map((l) => l.filevineEntity.id),
    ]);

    const unlinkedFinchCount = finchEntities.filter(
      (e) => !linkedFinchIds.has(e.id),
    ).length;
    const unlinkedFilevineCount = filevineEntities.filter(
      (e) => !linkedFilevineIds.has(e.id),
    ).length;

    return {
      sortedFinchEntities,
      sortedFilevineEntities,
      approvedLinks: resolvedApproved,
      suggestedLinks: resolvedSuggested,
      counts: {
        approved: resolvedApproved.length,
        suggested: resolvedSuggested.length,
        unlinkedFinch: unlinkedFinchCount,
        unlinkedFilevine: unlinkedFilevineCount,
      },
    };
  }, [finchEntities, filevineEntities, suggestedLinks, approvedLinks]);

  const getLinkForEntity = useMemo(() => {
    const linkMap = new Map<string, ResolvedLink>();

    for (const link of state.approvedLinks) {
      linkMap.set(link.finchEntity.id, link);
      linkMap.set(link.filevineEntity.id, link);
    }
    for (const link of state.suggestedLinks) {
      // Don't overwrite approved links
      if (!linkMap.has(link.finchEntity.id)) {
        linkMap.set(link.finchEntity.id, link);
      }
      if (!linkMap.has(link.filevineEntity.id)) {
        linkMap.set(link.filevineEntity.id, link);
      }
    }

    return (entityId: string): ResolvedLink | undefined => {
      return linkMap.get(entityId);
    };
  }, [state.approvedLinks, state.suggestedLinks]);

  return {
    state,
    getLinkForEntity,
  };
}

export default useLinkingState;
